// SignUp.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { Loading, Toast } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";
import { useFormInput, formatIdNumber, formatBizNumber, maskName, maskPhone, onlyNumericInput, formatTime } from "@/utils";
import { mobileTypeOptions, branchTypeOptions } from "@/constants/selectOptions";
import { AuthRequestStatus, AuthCodeStatus } from "@/enum";
import { usePostJoinMutation } from "@/redux/joinApi";
import { useLazyGetRentCorpQuery, useGetAddressCodeQuery, useGetAddressCode2Mutation } from "@/redux/metaApi";

import {
    useLazyExistLoginIdQuery, // 아이디 중복확인
    useNicePhoneRequestMutation, // 인증번호 요청
    useNicePhoneVerifyMutation, // 인증번호 확인
    useLazyExistPhoneQuery, // 휴대폰번호 중복확인
} from "@/redux/authApi";

import EyeIcon from "@/assets/icons/icon-eyes.svg";
import logoImg from "@/assets/icons/icon-mobilicar-logo_bk.png";

export const SignUp = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const { formData, handleChange, setFormData } = useFormInput({
        toastMsg: "",
        // 팝업 상태
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        addressPopupStatus: false,
        businessCheckPopupStatus: false,
        headOfficeCheckPopupStatus: false,
        // Step 1: 이용약관 동의
        terms: {
            all: false,
            service: false,
            privacy: false,
            privacyThirdParty: false,
            corporate: false,
            promotion: false,
        },
        // Step 2: 기본 작성 폼
        loginId: "",
        password: "",
        passwordCheck: "",
        name: "",
        idNumber: "", // 주민번호
        mobileType: "",
        phone: "",
        // 본인 인증 관련
        phoneAuthId: null,
        authNum: "",
        authVerified: false,
        timer: 0,
        authCodeStatus: AuthCodeStatus.WAITING, // WAITING, IN_PROGRESS, TIMEOUT
        // Step 3: 추가 작성 (사업자 정보)
        rentCorp: {
            corpName: "",
            bizNumber: "",
            repName: "",
            corpPhone: "",
            email: "",
            address: "",
            addressDetail: "",
            bCode: "",
            parentRentCorpId: "",
        },
    });

    const [postJoin, { isLoading }] = usePostJoinMutation();

    useEffect(() => {
        if (localStorage.getItem("access_token")) {
            navigate("/");
        }
    }, [navigate]);

    const nextStep = () => setCurrentStep((prev) => prev + 1);
    const prevStep = () => setCurrentStep((prev) => prev - 1);

    return (
        <>
            {formData.toastMsg && <Toast visible={true} msg={formData.toastMsg} setFormData={setFormData} />}
            <div className="signup-container">
                <div className="signup-banner">
                    <span>
                        <img src={logoImg} alt="" />
                    </span>
                    <span>MOBILICAR PARTNERS</span>
                </div>
                <div className="signup-content">
                    {currentStep === 0 && (
                        <SignUpTerms formData={formData} handleChange={handleChange} setFormData={setFormData} nextStep={nextStep} />
                    )}
                    {currentStep === 1 && (
                        <SignUpForm
                            formData={formData}
                            handleChange={handleChange}
                            setFormData={setFormData}
                            nextStep={nextStep}
                            prevStep={prevStep}
                        />
                    )}
                    {currentStep === 2 && (
                        <SignUpAdditional
                            formData={formData}
                            handleChange={handleChange}
                            setFormData={setFormData}
                            prevStep={prevStep}
                            nextStep={async () => {
                                // 유효성 검사
                                const { rentCorp } = formData;
                                if (
                                    !rentCorp.corpName ||
                                    !rentCorp.bizNumber ||
                                    !rentCorp.repName ||
                                    !rentCorp.address ||
                                    !rentCorp.addressDetail ||
                                    !rentCorp.bCode
                                ) {
                                    setFormData((prev) => ({
                                        ...prev,
                                        toastMsg: "모든 필수 사업자 정보를 입력해주세요.",
                                    }));
                                    return;
                                }

                                // 지역 및 지역 구분 선택 유효성 검사
                                if (!formData.firstAddressCodeId || !formData.secondAddressCodeId || !formData.branchType) {
                                    setFormData((prev) => ({
                                        ...prev,
                                        toastMsg: "지역 및 지역 구분을 선택해주세요.",
                                    }));
                                    return;
                                }

                                // 회원가입 요청 바디
                                const joinBody = {
                                    phoneAuthId: formData.phoneAuthId,
                                    loginId: formData.loginId,
                                    password: formData.password,
                                    name: formData.name,
                                    rentCorp: {
                                        corpName: rentCorp.corpName,
                                        bizNumber: rentCorp.bizNumber,
                                        repName: rentCorp.repName,
                                        corpPhone: rentCorp.corpPhone,
                                        email: rentCorp.email,
                                        address: rentCorp.address,
                                        addressDetail: rentCorp.addressDetail,
                                        bCode: rentCorp.bCode,
                                        parentRentCorpId: formData.loginType === "BRANCH" ? Number(rentCorp.parentRentCorpId) : null,
                                    },
                                    firstAddressCodeId: formData.firstAddressCodeId,
                                    secondAddressCodeId: formData.secondAddressCodeId,
                                    branchType: formData.branchType,
                                };
                                try {
                                    const response = await postJoin(joinBody).unwrap();
                                    if (response) {
                                        nextStep();
                                    } else {
                                        setFormData((prev) => ({
                                            ...prev,
                                            toastMsg: response.message || "회원가입 실패",
                                        }));
                                    }
                                } catch (error) {
                                    setFormData((prev) => ({
                                        ...prev,
                                        toastMsg: error.message || "회원가입 실패",
                                    }));
                                }
                            }}
                        />
                    )}
                    {currentStep === 3 && <SignUpSuccess navigate={navigate} />}
                </div>
            </div>

            {formData.addressPopupStatus && (
                <SearchAddress
                    modalStatus={formData.addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => ({
                            ...prev,
                            rentCorp: { ...prev.rentCorp, address: data.address, bCode: data.bcode },
                        }))
                    }
                />
            )}

            {formData.businessCheckPopupStatus && (
                <BusinessCheckPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, businessCheckPopupStatus: false }))}
                    formData={formData}
                    handleChange={handleChange}
                    setFormData={setFormData}
                />
            )}

            {formData.headOfficeCheckPopupStatus && (
                <HeadOfficeCheckPopup
                    layer={true}
                    onClose={() => setFormData((prev) => ({ ...prev, headOfficeCheckPopupStatus: false }))}
                    formData={formData}
                    handleChange={handleChange}
                    setFormData={setFormData}
                />
            )}
        </>
    );
};

// ===================== Step 1: SignUpTerms =====================
const SignUpTerms = ({ formData, handleChange, setFormData, nextStep }) => {
    const requiredTerms = ["service", "privacy", "privacyThirdParty", "corporate"];
    const allRequiredChecked = requiredTerms.every((term) => formData.terms[term]);

    // 전체동의
    const handleAllChange = (e) => {
        const checked = e.target.checked;
        setFormData((prev) => ({
            ...prev,
            terms: {
                all: checked,
                service: checked,
                privacy: checked,
                privacyThirdParty: checked,
                corporate: checked,
                promotion: checked, // 선택 항목도 토글
            },
        }));
    };

    // 개별 항목
    const handleIndividualChange = (e) => {
        const { id, checked } = e.target;
        const newTerms = { ...formData.terms, [id]: checked };
        // 모두 true면 all: true, 아니면 false
        const allTrue = newTerms.service && newTerms.privacy && newTerms.privacyThirdParty && newTerms.corporate && newTerms.promotion;
        setFormData((prev) => ({
            ...prev,
            terms: {
                ...newTerms,
                all: allTrue,
            },
        }));
    };

    const handleNext = () => {
        if (!allRequiredChecked) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "필수 약관에 모두 동의해주세요.",
            }));
            return;
        }
        nextStep();
    };

    return (
        <div className="app-page">
            <div className="page-header">
                <span className="header-tit">이용약관 동의</span>
            </div>
            <div className="page-content">
                <div className="auth-container">
                    <div className="term-container">
                        <div className="term-content">
                            <div className="term-list term-all">
                                <div className="term-item">
                                    <div className="checkbox">
                                        <input
                                            type="checkbox"
                                            id="all"
                                            value="TERMS_ALL"
                                            key="TERMS_ALL"
                                            checked={formData.terms.all}
                                            onChange={handleAllChange}
                                        />
                                        <label htmlFor="all">
                                            <span>전체동의</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div className="term-list">
                                <div className="term-item">
                                    <div className="checkbox">
                                        <input
                                            type="checkbox"
                                            id="service"
                                            value="PRIVACY_AGREE1"
                                            key="PRIVACY_AGREE1"
                                            checked={formData.terms.service}
                                            onChange={handleIndividualChange}
                                        />
                                        <label htmlFor="service">
                                            <span>[필수] 서비스 이용약관</span>
                                        </label>
                                        <a
                                            href="https://immense-property-46a.notion.site/1d0b1cacbbd580ae9393c7e0000fc2e9?pvs=4"
                                            target="_blank"
                                        >
                                            보기
                                        </a>
                                    </div>
                                </div>
                                <div className="term-item">
                                    <div className="checkbox">
                                        <input
                                            type="checkbox"
                                            id="privacy"
                                            value="PRIVACY_AGREE2"
                                            key="PRIVACY_AGREE2"
                                            checked={formData.terms.privacy}
                                            onChange={handleIndividualChange}
                                        />
                                        <label htmlFor="privacy">
                                            <span>[필수] 개인정보 처리방침</span>
                                        </label>
                                        <a
                                            href="https://immense-property-46a.notion.site/1c2b1cacbbd58096b194cbce0b4f27b9?pvs=4"
                                            target="_blank"
                                        >
                                            보기
                                        </a>
                                    </div>
                                </div>
                                <div className="term-item">
                                    <div className="checkbox">
                                        <input
                                            type="checkbox"
                                            id="privacyThirdParty"
                                            value="PRIVACY_AGREE3"
                                            key="PRIVACY_AGREE3"
                                            checked={formData.terms.privacyThirdParty}
                                            onChange={handleIndividualChange}
                                        />
                                        <label htmlFor="privacyThirdParty">
                                            <span>[필수] 개인정보 제3자 제공</span>
                                        </label>
                                        <a
                                            href="https://immense-property-46a.notion.site/3-1d0b1cacbbd580829aebe2fab5231c17?pvs=4"
                                            target="_blank"
                                        >
                                            보기
                                        </a>
                                    </div>
                                </div>
                                <div className="term-item">
                                    <div className="checkbox">
                                        <input
                                            type="checkbox"
                                            id="promotion"
                                            value="PRIVACY_AGREE4"
                                            key="PRIVACY_AGREE4"
                                            checked={formData.terms.promotion}
                                            onChange={handleIndividualChange}
                                        />
                                        <label htmlFor="promotion">
                                            <span>[선택] 프로모션 정보 수신 동의</span>
                                        </label>
                                        <a
                                            href="https://immense-property-46a.notion.site/1d0b1cacbbd580c0a0f8f314a055971e?pvs=4"
                                            target="_blank"
                                        >
                                            보기
                                        </a>
                                    </div>
                                </div>
                                <div className="term-item">
                                    <div className="checkbox">
                                        <input
                                            type="checkbox"
                                            id="corporate"
                                            value="PRIVACY_AGREE5"
                                            key="PRIVACY_AGREE5"
                                            checked={formData.terms.corporate}
                                            onChange={handleIndividualChange}
                                        />
                                        <label htmlFor="corporate">
                                            <span>[필수] 법인 아이디 이용 시 주의사항에 대한 동의</span>
                                        </label>
                                    </div>
                                    <p>{`* 법인 아이디는 관리자와 직원들이 하나의 ID와 계정을 공유하는 서비스로 관리자와 직원은 서비스 이용 및 모든 활동에 대한 책임을 공유합니다.`}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="form-button">
                        <div className="button-wrapper">
                            <button
                                className={`btn btn-md button-login ${allRequiredChecked ? "btn-primary" : "btn-disabled"}`}
                                onClick={handleNext}
                            >
                                회원가입 진행
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ===================== Step 2: SignUpForm =====================
const SignUpForm = ({ formData, handleChange, setFormData, nextStep, prevStep }) => {
    useEffect(() => {
        console.log(formData);
    }, [formData]);

    const timerRef = useRef(null);

    // 중복확인 API (lazy query)
    const [triggerExistLoginId, { data: dupData, isFetching, error }] = useLazyExistLoginIdQuery();

    // 휴대폰 인증 API
    const [nicePhoneRequest] = useNicePhoneRequestMutation();
    const [nicePhoneVerify] = useNicePhoneVerifyMutation();

    // 휴대폰번호 중복확인 API
    const [triggerExistPhone] = useLazyExistPhoneQuery();

    // 타이머 시작
    const startTimer = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setFormData((prev) => ({ ...prev, timer: 180, authCodeStatus: AuthCodeStatus.IN_PROGRESS }));
        timerRef.current = setInterval(() => {
            setFormData((prev) => {
                if (prev.timer <= 1) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                    return { ...prev, timer: 0, authCodeStatus: AuthCodeStatus.TIMEOUT };
                }
                return { ...prev, timer: prev.timer - 1 };
            });
        }, 1000);
    }, [setFormData]);

    useEffect(() => {
        // 언마운트 시 타이머 해제
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    const validateForm = async () => {
        // if (!formData.loginType) {
        //     setFormData((prev) => ({ ...prev, toastMsg: "가입유형을 선택해주세요." }));
        //     return false;
        // }
        // if (formData.loginType === "BRANCH") {
        //     if (!formData.rentCorp.parentRentCorpName) {
        //         setFormData((prev) => ({ ...prev, toastMsg: "본점 정보를 선택해주세요." }));
        //         return false;
        //     }
        // }
        if (!formData.loginId) {
            setFormData((prev) => ({ ...prev, toastMsg: "아이디를 입력해주세요." }));
            return false;
        }
        if (formData.loginId.length < 4) {
            setFormData((prev) => ({ ...prev, toastMsg: "아이디는 4자 이상 입력해주세요." }));
            return false;
        }
        if (!formData.idDuplicateCheck) {
            setFormData((prev) => ({ ...prev, toastMsg: "아이디 중복확인을 진행해주세요." }));
            return false;
        }
        if (!formData.password) {
            setFormData((prev) => ({ ...prev, toastMsg: "비밀번호를 입력해주세요." }));
            return false;
        }
        if (formData.password !== formData.passwordCheck) {
            setFormData((prev) => ({ ...prev, toastMsg: "비밀번호가 일치하지 않습니다." }));
            return false;
        }
        // ✅ 비밀번호 복잡성 검사
        const password = formData.password;
        const hasLetter = /[a-zA-Z]/.test(password);
        const hasNumber = /\d/.test(password);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        if (password.length < 8 || !hasLetter || !hasNumber || !hasSpecial) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "비밀번호는 영문, 숫자, 특수문자를 모두 포함한 8자 이상으로 설정해주세요.",
            }));
            return false;
        }
        if (!formData.name) {
            setFormData((prev) => ({ ...prev, toastMsg: "이름을 입력해주세요." }));
            return false;
        }
        if (!formData.idNumber) {
            setFormData((prev) => ({ ...prev, toastMsg: "주민번호 13자리를 입력해주세요." }));
            return false;
        }
        if (!formData.mobileType) {
            setFormData((prev) => ({ ...prev, toastMsg: "통신사를 선택해주세요." }));
            return false;
        }
        if (!formData.phone) {
            setFormData((prev) => ({ ...prev, toastMsg: "휴대폰 번호를 입력해주세요." }));
            return false;
        }
        if (!formData.authVerified) {
            setFormData((prev) => ({ ...prev, toastMsg: "휴대폰 인증을 완료해주세요." }));
            return false;
        }

        // 휴대폰 중복 확인
        try {
            const result = await triggerExistPhone({ phone: formData.phone }).unwrap();
            return true;
        } catch (error) {
            if (error.status === 417) {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "이미 가입된 휴대폰번호입니다.",
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: error.message || "휴대폰번호 중복확인에 실패했습니다.",
                }));
            }
            return false;
        }
    };

    // 아이디 중복확인 버튼
    const handleCheckDuplicate = async () => {
        if (!formData.loginId) {
            setFormData((prev) => ({ ...prev, toastMsg: "아이디를 입력해주세요." }));
            return;
        }
        const result = await triggerExistLoginId({ login_id: formData.loginId });
        if (result?.data) {
            // 만약 API가 "중복이면 에러, 사용가능이면 result=1" 형태라면, 아래 로직을 수정해야 할 수도 있음
            setFormData((prev) => ({
                ...prev,
                toastMsg: "사용 가능한 아이디입니다.",
                idDuplicateCheck: true,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                toastMsg: result?.error?.message || "이미 사용 중인 아이디입니다.",
                idDuplicateCheck: false,
            }));
        }
    };

    // 인증번호 요청
    const handleRequestAuth = async () => {
        if (!formData.name || !formData.idNumber || !formData.mobileType || !formData.phone) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "이름, 주민번호, 통신사, 휴대폰 번호를 모두 입력해주세요.",
            }));
            return;
        }

        try {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            const resp = await nicePhoneRequest({
                idNumber: formData.idNumber?.substring(0, 7), // 주민번호 앞 7자리만 전달 (6자리 + 뒷자리 첫번째)
                mobileType: formData.mobileType,
                phone: formData.phone,
                name: formData.name,
            }).unwrap();
            setFormData((prev) => ({
                ...prev,
                phoneAuthId: resp.data?.id || 0,
                authVerified: false,
                toastMsg: "인증번호가 발송되었습니다.",
                authCodeStatus: AuthCodeStatus.WAITING,
            }));
            startTimer();
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "인증번호 요청에 실패했습니다.",
            }));
        }
    };

    // 인증번호 확인
    const handleVerify = async () => {
        if (!formData.authNum) {
            setFormData((prev) => ({ ...prev, toastMsg: "인증번호를 입력해주세요." }));
            return;
        }
        if (!formData.phoneAuthId) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "인증번호 요청을 먼저 해주세요.",
            }));
            return;
        }
        try {
            await nicePhoneVerify({
                id: formData.phoneAuthId,
                authNum: formData.authNum,
            }).unwrap();
            // 성공 처리
            setFormData((prev) => ({
                ...prev,
                authVerified: true,
                toastMsg: "본인 인증이 완료되었습니다.",
            }));
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "인증에 실패했습니다.",
            }));
        }
    };

    const handleNext = async () => {
        const isValid = await validateForm();
        if (isValid) {
            nextStep();
        }
    };

    return (
        <div className="app-page">
            <div className="page-header">
                <span className="header-tit">회원가입</span>
            </div>
            <div className="page-content">
                <div className="auth-container">
                    <div className="form-wrapper">
                        {/* 가입유형 */}
                        {/* <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">
                                    가입유형 <em>*</em>
                                </span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="item-radio" style={{ display: "flex", alignItems: "center", minHeight: "48px" }}>
                                        <div className="input-radio">
                                            <div className="radiobox">
                                                <input
                                                    type="radio"
                                                    id="loginType_HEAD_OFFICE"
                                                    name="loginType"
                                                    value="HEAD_OFFICE"
                                                    onChange={handleChange}
                                                    checked={formData.loginType === "HEAD_OFFICE"}
                                                />
                                                <label htmlFor="loginType_HEAD_OFFICE">본사 회원가입</label>
                                            </div>
                                            <div className="radiobox">
                                                <input
                                                    type="radio"
                                                    id="loginType_BRANCH"
                                                    name="loginType"
                                                    value="BRANCH"
                                                    onChange={handleChange}
                                                    checked={formData.loginType === "BRANCH"}
                                                />
                                                <label htmlFor="loginType_BRANCH">지점 회원가입</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div> */}
                        {/* 본점 정보(선택) */}
                        {/* {formData.loginType === "BRANCH" && (
                            <div className="form-item">
                                <div className="item-label">
                                    <span className="label-title">
                                        본점 정보 <em></em>
                                    </span>
                                </div>
                                <div className="item-content">
                                    <div className="content-field">
                                        <div className="field-row">
                                            <input
                                                type="text"
                                                placeholder="본사 사업자명을 입력해주세요."
                                                name="rentCorp.parentRentCorpName"
                                                value={formData.rentCorp.parentRentCorpName}
                                                onChange={handleChange}
                                                // readOnly
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-xs"
                                            onClick={() =>
                                                setFormData((prevState) => ({
                                                    ...prevState,
                                                    headOfficeCheckPopupStatus: true,
                                                }))
                                            }
                                        >
                                            본점선택
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )} */}
                        {/* 아이디 */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">
                                    아이디 <em>*</em>
                                </span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="아이디를 입력해주세요."
                                            name="loginId"
                                            value={formData.loginId}
                                            onChange={(e) => {
                                                // 한글 입력 방지
                                                const value = e.target.value.replace(/[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/g, "");
                                                handleChange({
                                                    target: {
                                                        name: e.target.name,
                                                        value,
                                                    },
                                                });
                                            }}
                                        />
                                    </div>
                                    <button type="button" className="btn btn-primary btn-xs" onClick={handleCheckDuplicate}>
                                        중복확인
                                    </button>
                                </div>
                            </div>
                        </div>
                        {/* 비밀번호 */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">
                                    비밀번호 <em>*</em>
                                </span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <input
                                            type="password"
                                            placeholder="비밀번호를 입력해주세요."
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                        />
                                        {/* <span className="input-esset">
                                            <img src={EyeIcon} alt="" />
                                        </span> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* 비밀번호 확인 */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">
                                    비밀번호 확인 <em>*</em>
                                </span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <input
                                            type="password"
                                            placeholder="비밀번호를 재입력해주세요."
                                            name="passwordCheck"
                                            value={formData.passwordCheck}
                                            onChange={handleChange}
                                        />
                                        {/* <span className="input-esset">
                                            <img src={EyeIcon} alt="" />
                                        </span> */}
                                    </div>
                                </div>
                                <div className="content-helper">
                                    <span className="helper-text">Helper Text</span>
                                </div>
                            </div>
                        </div>
                        {/* 이름 */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">
                                    이름 <em>*</em>
                                </span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="이름을 입력해주세요."
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* 주민번호  */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">
                                    주민등록번호 <em>*</em>
                                </span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="6자리"
                                            name="idNumber"
                                            value={formData.idNumber?.slice(0, 6) || ""}
                                            onChange={(e) => {
                                                const firstSix = e.target.value;
                                                const lastSeven = formData.idNumber?.slice(6) || "";
                                                handleChange({
                                                    target: {
                                                        name: "idNumber",
                                                        value: firstSix + lastSeven,
                                                    },
                                                });
                                            }}
                                            onInput={onlyNumericInput}
                                            maxLength={6}
                                        />
                                    </div>
                                    <span className="input-esset">-</span>
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="1자리"
                                            name="idNumber"
                                            value={formData.idNumber?.slice(6) || ""}
                                            onChange={(e) => {
                                                const firstSix = formData.idNumber?.slice(0, 6) || "";
                                                const lastSeven = e.target.value;
                                                handleChange({
                                                    target: {
                                                        name: "idNumber",
                                                        value: firstSix + lastSeven,
                                                    },
                                                });
                                            }}
                                            onInput={onlyNumericInput}
                                            maxLength={1}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* 휴대폰 번호 */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">
                                    휴대폰 번호 <em>*</em>
                                </span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row" style={{ width: "100px", flexShrink: "0" }}>
                                        <Select
                                            options={mobileTypeOptions}
                                            className="custom-select"
                                            classNamePrefix="custom-select"
                                            isSearchable={false}
                                            placeholder="선택"
                                            value={mobileTypeOptions.find((option) => option.value === formData.mobileType)}
                                            onChange={(option) => setFormData((prev) => ({ ...prev, mobileType: option.value }))}
                                        />
                                    </div>
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="-없이 휴대폰번호 입력"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            onInput={onlyNumericInput}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-xs btn-primary"
                                        onClick={handleRequestAuth}
                                        disabled={formData.authVerified}
                                    >
                                        {formData.authCodeStatus === AuthCodeStatus.IN_PROGRESS ? "재요청" : "인증번호 요청"}
                                    </button>
                                </div>
                                <div className="content-helper">
                                    <span className="helper-text">Helper Text</span>
                                </div>
                                {/* 인증번호 입력칸 */}
                                {formData.authCodeStatus !== AuthCodeStatus.WAITING && (
                                    <div className="item-content">
                                        <div className="content-field">
                                            <div className="field-row">
                                                <input
                                                    type="text"
                                                    placeholder="인증번호를 입력해주세요."
                                                    name="authNum"
                                                    value={formData.authNum}
                                                    onChange={handleChange}
                                                    onInput={onlyNumericInput}
                                                />
                                                <span className="input-esset color-error">
                                                    {formData.timer > 0 ? formatTime(formData.timer) : "만료"}
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-xs btn-primary"
                                                onClick={async () => {
                                                    console.log(formData.timer);
                                                    if (formData.timer <= 0) {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            toastMsg: "인증시간이 만료되었습니다.",
                                                        }));
                                                        return;
                                                    }
                                                    if (!formData.authNum) {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            toastMsg: "인증번호를 입력해주세요.",
                                                        }));
                                                        return;
                                                    }
                                                    if (!formData.phoneAuthId) {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            toastMsg: "인증번호 요청을 먼저 해주세요.",
                                                        }));
                                                        return;
                                                    }
                                                    try {
                                                        await nicePhoneVerify({
                                                            id: formData.phoneAuthId,
                                                            authNum: formData.authNum,
                                                        }).unwrap();
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            authVerified: true,
                                                            toastMsg: "본인 인증이 완료되었습니다.",
                                                        }));
                                                        if (timerRef.current) {
                                                            clearInterval(timerRef.current);
                                                            timerRef.current = null;
                                                        }
                                                    } catch (error) {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            toastMsg: error.message || "인증에 실패했습니다.",
                                                        }));
                                                    }
                                                }}
                                                disabled={formData.authVerified}
                                            >
                                                인증번호 확인
                                            </button>
                                        </div>
                                        <div className="content-helper">
                                            <span className="helper-text">Helper Text</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* 이전/다음 버튼 */}
                    <div className="form-button">
                        <div className="button-wrapper">
                            <button className="btn btn-md btn-outline" onClick={prevStep}>
                                이전
                            </button>
                            <button className="btn btn-md btn-primary" onClick={handleNext}>
                                다음
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ===================== Step 3: SignUpAdditional =====================
const SignUpAdditional = ({ formData, handleChange, setFormData, prevStep, nextStep }) => {
    const handleSubmit = async () => {
        nextStep(); // 회원가입 로직은 상위에서 실행
    };

    const { data: loc1, isLoading: isLoadingLoc1 } = useGetAddressCodeQuery({ take_1_step: true, take_2_step: false });
    const [getAddressCode2, { data: loc2, isLoadingLoc2 }] = useGetAddressCode2Mutation();

    return (
        <div className="app-page">
            <div className="page-header">
                <span className="header-tit">회원가입</span>
            </div>
            <div className="page-content">
                <div className="auth-container">
                    <div className="form-wrapper">
                        {/* 사업자명 */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">
                                    사업자명 <em>*</em>
                                </span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="사업자명을 입력해주세요."
                                            name="rentCorp.corpName"
                                            value={formData.rentCorp.corpName}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* 사업자 등록번호 */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">
                                    사업자 등록번호 <em>*</em>
                                </span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="사업자 등록번호를 입력해주세요."
                                            name="rentCorp.bizNumber"
                                            value={formatBizNumber(formData.rentCorp.bizNumber)}
                                            onChange={handleChange}
                                            onInput={onlyNumericInput}
                                            maxLength={12}
                                        />
                                    </div>
                                    {formData.loginType === "HEAD_OFFICE" && (
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-xs"
                                            onClick={() =>
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    businessCheckPopupStatus: true,
                                                }))
                                            }
                                        >
                                            가입자 확인
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* 대표자 이름 */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">
                                    대표자 이름 <em>*</em>
                                </span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="대표자 이름을 입력해주세요."
                                            name="rentCorp.repName"
                                            value={formData.rentCorp.repName}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* 업체 대표 번호 */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">업체 대표 번호</span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="업체 대표 번호를 입력해주세요."
                                            name="rentCorp.corpPhone"
                                            value={formData.rentCorp.corpPhone}
                                            onChange={handleChange}
                                            onInput={onlyNumericInput}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* 이메일 주소 */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">이메일 주소 (세금계산서 발행용)</span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="이메일 주소를 입력해주세요."
                                            name="rentCorp.email"
                                            value={formData.rentCorp.email}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* 사업자 주소 */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">
                                    사업자 주소 <em>*</em>
                                </span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="사업자 주소를 입력해주세요."
                                            name="rentCorp.address"
                                            value={formData.rentCorp.address}
                                            onChange={handleChange}
                                            onClick={() => setFormData((prev) => ({ ...prev, addressPopupStatus: true }))}
                                            readOnly
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-xs"
                                        onClick={() => setFormData((prev) => ({ ...prev, addressPopupStatus: true }))}
                                    >
                                        검색
                                    </button>
                                </div>
                            </div>
                        </div>
                        {/* 상세 주소 */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">
                                    상세 주소 <em>*</em>
                                </span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <input
                                            type="text"
                                            placeholder="상세 주소를 입력해주세요."
                                            name="rentCorp.addressDetail"
                                            value={formData.rentCorp.addressDetail}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* 지역 설정 */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">
                                    지역 설정 <em>*</em>
                                </span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <Select
                                            options={loc1?.data.map((item) => ({
                                                value: item.id,
                                                label: item.address1,
                                                bcode: item.bCode,
                                            }))}
                                            value={
                                                formData.firstAddressCodeId
                                                    ? loc1?.data
                                                          .map((item) => ({
                                                              value: item.id,
                                                              label: item.address1,
                                                              bcode: item.bCode,
                                                          }))
                                                          .find((option) => option.value === formData.firstAddressCodeId)
                                                    : null
                                            }
                                            className="custom-select"
                                            classNamePrefix="custom-select"
                                            isSearchable
                                            placeholder="1차 지역"
                                            onChange={(option) => {
                                                getAddressCode2({ take_1_step: false, take_2_step: true, parent_b_code: option.bcode });
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    firstAddressCodeId: option.value,
                                                    secondAddressCodeId: null,
                                                }));
                                            }}
                                        />
                                    </div>
                                    <div className="field-row">
                                        <Select
                                            options={loc2?.data.map((item) => ({
                                                value: item.id,
                                                label: item.address2,
                                                bcode: item.bCode,
                                            }))}
                                            value={
                                                formData.secondAddressCodeId
                                                    ? loc2?.data
                                                          .map((item) => ({
                                                              value: item.id,
                                                              label: item.address2,
                                                              bcode: item.bCode,
                                                          }))
                                                          .find((option) => option.value === formData.secondAddressCodeId)
                                                    : null
                                            }
                                            className="custom-select"
                                            classNamePrefix="custom-select"
                                            isSearchable
                                            placeholder="2차 지역"
                                            onChange={(option) => {
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    secondAddressCodeId: option.value,
                                                }));
                                            }}
                                            isDisabled={!formData.firstAddressCodeId}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* 지역 구분 */}
                        <div className="form-item">
                            <div className="item-label">
                                <span className="label-title">
                                    지역 구분 <em>*</em>
                                </span>
                            </div>
                            <div className="item-content">
                                <div className="content-field">
                                    <div className="field-row">
                                        <Select
                                            options={branchTypeOptions}
                                            className="custom-select"
                                            classNamePrefix="custom-select"
                                            isSearchable={false}
                                            placeholder="선택"
                                            value={branchTypeOptions.find((option) => option.value === formData.branchType)}
                                            onChange={(option) => setFormData((prev) => ({ ...prev, branchType: option.value }))}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* 이전/가입완료 버튼 */}
                    <div className="form-button">
                        <div className="button-wrapper">
                            <button className="btn btn-md btn-outline" onClick={prevStep}>
                                이전
                            </button>
                            <button className="btn btn-md btn-primary" onClick={handleSubmit}>
                                가입완료
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ===================== Step 4: SignUpSuccess =====================
const SignUpSuccess = ({ navigate }) => {
    return (
        <div className="app-page" style={{ alignContent: "center" }}>
            <div className="page-header">
                <span className="header-tit">회원가입 신청 완료</span>
                <span className="header-sub_txt">
                    로그인 후 추가 정보 입력 및 기본 서류를 업로드
                    <br />
                    하시면 서비스 이용이 가능합니다.
                </span>
            </div>
            <div className="page-content">
                <div className="auth-container">
                    <div className="txt-box">
                        <div className="success-id">
                            <span className="register-id">고객센터 운영시간</span>
                            <span className="register-date">10시 ~ 18시</span>
                        </div>
                        <div className="success-id">
                            <span className="register-id">대표번호</span>
                            <span className="register-date">1800-6586</span>
                        </div>
                        <div className="success-id">
                            <span className="register-id">
                                이메일 <em>(담당자가 확인 후 회신 드립니다.)</em>
                            </span>
                            <span className="register-date">info@mobilicar.kr</span>
                        </div>
                    </div>
                    <div className="form-button">
                        <div className="button-wrapper">
                            <button className="btn btn-primary btn-md button-login" onClick={() => navigate("/login")}>
                                로그인 하러가기
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ===================== 팝업 컴포넌트들 =====================
const ResetPasswordConfirmPopup = ({ layer = true, onClose }) => {
    useEffect(() => {
        document.body.classList.add("scroll-lock");
        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);
    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>
            <div className="popup confirm-popup">
                <div className="popup-content">
                    <div className="content-title">
                        <span>비밀번호가 변경되었어요!</span>
                    </div>
                    <div className="content-description">
                        <p>변경한 비밀번호로 로그인을 진행해보세요.</p>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button className="btn btn-primary btn-close" onClick={onClose}>
                        확인
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};

const BusinessCheckPopup = ({ layer = true, onClose, formData, setFormData }) => {
    const [query, setQuery] = useState("");
    const [trigger, { data, error, isLoading }] = useLazyGetRentCorpQuery();

    useEffect(() => {
        if (formData.rentCorp.bizNumber) {
            setQuery(formData.rentCorp.bizNumber);
            trigger({ office_type: "BRANCH", biz_number: formData.rentCorp.bizNumber });
        }
    }, []);

    const handleSearch = () => {
        trigger({ office_type: "BRANCH", biz_number: query });
    };

    useEffect(() => {
        document.body.classList.add("scroll-lock");
        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>
            <div className="popup service-popup">
                <div className="popup-head">
                    <div className="head-tit">
                        <span className="tit-main">사업자 회원 검색</span>
                    </div>
                    <div className="btn-close" onClick={onClose}></div>
                </div>
                <div className="popup-content">
                    <div className="table-container">
                        <div className="table-filter">
                            <div className="filter-row">
                                <div className="filter-item">
                                    <div className="item-content">
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder="사업자 번호를 입력하세요."
                                                name="query"
                                                value={query}
                                                onChange={(e) => setQuery(e.target.value)}
                                            />
                                        </div>
                                        <div className="item-buttons">
                                            <button type="button" className="btn-search" onClick={handleSearch}>
                                                검색
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="table-content">
                            <div className="table-wrapper">
                                <div className="table">
                                    <table>
                                        <colgroup>
                                            <col width={"21%"} />
                                            <col width={"43%"} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">대표자</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">휴대폰번호</div>
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data && data.data && data.data.length > 0 ? (
                                                data.data.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{maskName(item.repName)}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{maskPhone(item.phone)}</div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="2" className="data-empty">
                                                        <div className="cell" data-align="center">
                                                            <span className="cell-desc">데이터가 없습니다.</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button className="btn btn-primary btn-confirm" onClick={onClose}>
                        확인
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};

const HeadOfficeCheckPopup = ({ layer = true, onClose, formData, setFormData }) => {
    const [selectedId, setSelectedId] = useState(formData.rentCorp.parentRentCorpId || null);
    const [query, setQuery] = useState("");
    const [trigger, { data, error, isLoading }] = useLazyGetRentCorpQuery();

    useEffect(() => {
        if (formData.rentCorp.parentRentCorpName) {
            setQuery(formData.rentCorp.parentRentCorpName);
            trigger({ office_type: "HEAD_OFFICE", query: formData.rentCorp.parentRentCorpName });
        }
    }, []);

    const handleSearch = () => {
        trigger({ office_type: "HEAD_OFFICE", query });
    };

    useEffect(() => {
        document.body.classList.add("scroll-lock");
        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    const handleConfirm = () => {
        const selectedItem = data?.data?.find((item) => item.id === selectedId);
        setFormData((prev) => ({
            ...prev,
            rentCorp: {
                ...prev.rentCorp,
                parentRentCorpId: selectedId,
                parentRentCorpName: selectedItem ? selectedItem.corpName : "",
            },
        }));
        onClose();
    };

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>
            <div className="popup service-popup">
                <div className="popup-head">
                    <div className="head-tit">
                        <span className="tit-main">본사 선택</span>
                    </div>
                    <div className="btn-close" onClick={onClose}></div>
                </div>
                <div className="popup-content">
                    <div className="table-container">
                        <div className="table-filter">
                            <div className="filter-row">
                                <div className="filter-item">
                                    <div className="item-content">
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder="입력"
                                                value={query || ""}
                                                onChange={(e) => setQuery(e.target.value)}
                                            />
                                        </div>
                                        <div className="item-buttons">
                                            <button type="button" className="btn-search" onClick={handleSearch}>
                                                검색
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="table-content">
                            <div className="table-wrapper">
                                <div className="table">
                                    <table>
                                        <colgroup>
                                            <col width={"12%"} />
                                            <col width={"32%"} />
                                            <col width={"17%"} />
                                            <col width={"39%"} />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">선택</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">사업자명</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">대표자</div>
                                                    </div>
                                                </th>
                                                <th>
                                                    <div className="cell">
                                                        <div className="cell-tit">지역</div>
                                                    </div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data && data.data && data.data.length > 0 ? (
                                                data.data.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="input-checkbox">
                                                                    <div className="checkbox">
                                                                        <input
                                                                            type="checkbox"
                                                                            id={`parentRentCorpId_${item.id}`}
                                                                            name="parentRentCorpId"
                                                                            checked={selectedId === item.id}
                                                                            onChange={() =>
                                                                                setSelectedId(selectedId === item.id ? null : item.id)
                                                                            }
                                                                        />
                                                                        <label htmlFor={`parentRentCorpId_${item.id}`}></label>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{item.corpName}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">{item.repName}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="cell">
                                                                <div className="cell-desc">
                                                                    {item.address}&nbsp;{item.addressDetail}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="data-empty">
                                                        <div className="cell" data-align="center">
                                                            <span className="cell-desc">데이터가 없습니다.</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button className="btn btn-primary btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button className="btn btn-primary btn-confirm" onClick={handleConfirm}>
                        완료
                    </button>
                </div>
            </div>
        </>,
        document.body
    );
};
